import { makeStyles } from '@material-ui/styles'
import type { KeyboardEvent } from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { topLeft } from 'core/elements/menu/defaults'
import MenuItem from 'core/elements/menu/MenuItem'
import PortalMenu from 'core/elements/menu/PortalMenu'
import Text from 'core/elements/Text'
import Tooltip from 'core/elements/tooltip'
import useToggler from 'core/hooks/useToggler'
import type Theme from 'core/themes/model'
import IconButton from '../button/IconButton'
import { BatchActionsContent } from './BatchActionsContent'
import { ACTION_BAR_HEIGHT } from './gridConstants'
import type { GridBatchActionGroupProps, GridBatchActionProps } from './hooks/useGridSelectableRows'

export type BatchActionBarVariant = 'floating' | 'sticky-top' | 'sticky-bottom'

const variantLabel: Record<BatchActionBarVariant, string> = {
  'sticky-top': 'Top of table',
  'sticky-bottom': 'Bottom of table',
  floating: 'Float at the bottom',
}

export const DEFAULT_BATCH_ACTION_BAR_VARIANT: BatchActionBarVariant = 'floating'

// Persisted cross-session state (localStorage) can be stale/corrupted from an
// older build or manual edit — never trust it as a valid variant unchecked.
export const isBatchActionBarVariant = (value: unknown): value is BatchActionBarVariant =>
  Object.hasOwn(variantLabel, value as string)

const onActivateKey = (handler: () => void) => (e: KeyboardEvent) =>
  (e.key === 'Enter' || e.key === ' ') && handler()

interface FloatingBatchActionBarProps<T> {
  selectedCount?: number
  clearSelectedRows?: () => void
  batchActions?: GridBatchActionProps<T>[]
  dropdownBatchActions?: GridBatchActionGroupProps<T>[]
  isReadOnly?: boolean
  variant?: BatchActionBarVariant
  onVariantChange?: (variant: BatchActionBarVariant) => void
}

const useStyles = makeStyles<Theme>((theme) => ({
  '@keyframes slideUp': {
    from: {
      opacity: 0,
      transform: 'translateX(-50%) translateY(16px)',
    },
    to: {
      opacity: 1,
      transform: 'translateX(-50%) translateY(0)',
    },
  },
  floatingActionRoot: {
    position: 'fixed',
    bottom: theme.spacing(3),
    left: '50%',
    padding: theme.spacing(1, 3),
    background: theme.components.table.toolbar,
    border: `1px solid ${theme.components.table.border}`,
    borderRadius: 4,
    boxShadow: '0 0 25px rgba(0,0,0,0.35)',
    maxWidth: 'calc(100% - 16px)',
    width: 'max-content',
    transform: 'translateX(-50%)',
    animation: '$slideUp 150ms ease-out',
  },
  stickyTopActionRoot: {
    position: 'sticky',
    top: 0,
    // Also pin horizontally: this bar is a plain sibling of the table (not a
    // table cell), so it isn't subject to the table's own horizontal scroll —
    // sticking left keeps it from drifting sideways when the table scrolls.
    left: 0,
    zIndex: 10, // 1 more than GridHeader's sticky gridHead (zIndex: 9)
    // Fixed height so GridHeader's sticky thead can stack directly below at
    // top: ACTION_BAR_HEIGHT instead of overlapping at top: 0.
    height: ACTION_BAR_HEIGHT,
    width: '100%',
    padding: theme.spacing(1, 2),
    background: theme.components.table.toolbar,
    borderTop: `1px solid ${theme.components.table.border}`,
    boxSizing: 'border-box',
  },
  stickyBottomActionRoot: {
    position: 'sticky',
    bottom: 0,
    left: 0,
    zIndex: 10,
    width: '100%',
    padding: theme.spacing(1, 2),
    background: theme.components.table.toolbar,
    borderTop: `1px solid ${theme.components.table.border}`,
    borderBottom: `1px solid ${theme.components.table.border}`,
    boxSizing: 'border-box',
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  selectedCount: {
    color: theme.components.table.toolbarColor,
    whiteSpace: 'nowrap',
  },
  clearBtn: {
    border: 'none',
  },
  divider: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    height: 28,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  dropdownActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  actionWrapper: {
    cursor: 'pointer',
  },
  actionWrapperDisabled: {
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  tooltip: {
    display: 'inline-block',
  },
}))

export default function FloatingBatchActionBar<T>({
  selectedCount,
  clearSelectedRows,
  batchActions,
  dropdownBatchActions,
  isReadOnly,
  variant = 'floating',
  onVariantChange,
}: FloatingBatchActionBarProps<T>) {
  const classes = useStyles()
  const [isVariantMenuOpen, toggleVariantMenu] = useToggler()

  if (!selectedCount) {
    return null
  }

  const variantRootClass: Record<BatchActionBarVariant, string> = {
    floating: classes.floatingActionRoot,
    'sticky-top': classes.stickyTopActionRoot,
    'sticky-bottom': classes.stickyBottomActionRoot,
  }
  const selectVariant = (nextVariant: BatchActionBarVariant) => () => {
    onVariantChange?.(nextVariant)
    toggleVariantMenu()
  }

  return (
    <div className={variantRootClass[variant]}>
      <div className={classes.inner}>
        <Text variant="body2" className={classes.selectedCount}>
          {`${selectedCount} selected`}
        </Text>
        <div className={classes.divider} />
        {!isReadOnly && (
          <div className={classes.actions}>
            <BatchActionsContent<T>
              batchActions={batchActions}
              dropdownBatchActions={dropdownBatchActions}
              renderDropdownOutside={variant !== 'sticky-top'}
              styledDropdownButton
              actionWrapperClassName={classes.actionWrapper}
              actionWrapperDisabledClassName={classes.actionWrapperDisabled}
              dropdownContainerClassName={classes.dropdownActions}
              tooltipClassName={classes.tooltip}
            />
          </div>
        )}
        {!isReadOnly && (batchActions?.length > 0 || dropdownBatchActions?.length > 0) && (
          <div className={classes.divider} />
        )}
        {onVariantChange && (
          <PortalMenu
            align={topLeft.align}
            offset={topLeft.offset}
            origin="top left"
            anchor={
              <Tooltip
                message="Toolbar Position"
                align={{
                  horizontal: 'middle',
                  vertical: 'bottom',
                }}
              >
                <FontAwesomeIcon size="md" solid>
                  ellipsis-vertical
                </FontAwesomeIcon>
              </Tooltip>
            }
            open={isVariantMenuOpen}
            onClose={toggleVariantMenu}
            onClick={toggleVariantMenu}
            onKeyDown={onActivateKey(toggleVariantMenu)}
            role="button"
            tabIndex={0}
            aria-label="Toolbar Position"
          >
            {(Object.keys(variantLabel) as BatchActionBarVariant[]).map((v) => (
              <MenuItem key={v} readonly={v === variant} onClick={selectVariant(v)}>
                {variantLabel[v]}
              </MenuItem>
            ))}
          </PortalMenu>
        )}
        <IconButton
          className={classes.clearBtn}
          onClick={() => clearSelectedRows?.()}
          onKeyDown={onActivateKey(() => clearSelectedRows?.())}
          icon="times"
          solid={false}
          info="Clear Selection"
        />
      </div>
    </div>
  )
}
